/* eslint-disable no-nested-ternary */
/* eslint-disable arrow-body-style */
/* eslint-disable react/prop-types */
import React, { memo, useMemo } from 'react';
import moment from 'moment';
import _ from 'lodash';

import {
    Box,
    Grid,
    Stack,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Pagination,
    Autocomplete,
    CircularProgress,
    InputAdornment,
    IconButton
} from '@mui/material';

import { CheckCircleOutline, Cancel } from '@mui/icons-material';
import { AspyreDatePicker as DatePicker } from 'components/common/InputFields';
import { LocationDropdown } from 'components/reusable/Dropdowns';
import ExportColumnData from 'components/ExportColumnData';
import NoData from 'components/reusable/NoData';
import AmountCard from 'components/reusable/AmountCard';

import useAuth from 'hooks/useAuth';
import useSnackbar from 'hooks/useSnackbar';
import usePermission from 'hooks/usePermission';

import { useGetSessionClassroomsQuery } from 'store/services/school/classroom';
import { GLOBAL_DATE_FORMAT, CLASS, GLOBAL_DATE_TIME_FORMAT } from 'constant';
import { utcToLocal } from 'utils/time-conversion';
import { downloadFile } from 'utils/file';
import CloseIcon from '@mui/icons-material/Close';
import { usePatientNameOrderValueGetter } from 'utils/patientNameOrder';

const DailyReport = ({ formik, dailyReportData, dailyReportLoading, setPage, page, download, downloadLoading, setDailyReportData }) => {
    const { user } = useAuth();
    const { notify } = useSnackbar();
    const { hasPermission } = usePermission();
    const patientNameOrderValueGetter = usePatientNameOrderValueGetter();

    const ROWS_PER_PAGE = 15;
    const service = CLASS.eidt;

    const cardData = [
        { label: 'Total Students', amount: dailyReportData?.fields?.[0]?.total_count || 0 },
        { label: 'Present Count', amount: dailyReportData?.fields?.[0]?.present_count || 0 },
        { label: 'Absent Count', amount: dailyReportData?.fields?.[0]?.absent_count || 0 },
        { label: 'Picked Up Count', amount: dailyReportData?.fields?.[0]?.pickup_count || 0 }
    ];

    const {
        data: classrooms,
        isLoading: classroomLoading,
        isFetching: classroomFetching
    } = useGetSessionClassroomsQuery(
        {
            filters: {
                ...(user?.client_id && { client_id: user.client_id }),
                ...(formik.values.location && { location_id: [formik.values.location.id] })
            }
        },
        { skip: !formik.values.location }
    );

    const sortedClassrooms = useMemo(() => {
        return [...(classrooms?.fields || [])].sort((a, b) => a.location.name.localeCompare(b.location.name));
    }, [classrooms]);

    const handleResetForm = () => {
        formik.resetForm();
        setDailyReportData(null);
    };

    const handleDownload = async () => {
        try {
            const res = await download({
                filters: {
                    client_id: user?.client_id,
                    location_id: formik.values.location?.id,
                    service: formik.values.location?.location_type?.location_type === 'EIDT' ? CLASS.eidt : CLASS.addt,
                    status: formik.values.status?.id,
                    start_date: moment(new Date(formik?.values?.startDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm'),
                    date: formik?.values?.startDate?.format('MM-DD-YYYY'),
                    end_date: moment(new Date(formik?.values?.startDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm'),
                    ...(formik.values.student && { student_name: formik.values.student }),
                    ...(formik.values.classroom && { class_room_id: formik.values.classroom?.id }),
                    is_download: true
                },
                pagination: { current_page: page }
            }).unwrap();

            downloadFile(res?.blob, res?.fileName);
        } catch (error) {
            notify({ message: 'Error downloading report', severity: 'error' });
        }
    };

    const renderTable = useMemo(() => {
        if (!dailyReportData?.fields?.length) return null;

        return (
            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {['Name', 'Classroom', 'Status', 'Present Time', 'Picked Up Time'].map((col, i) => (
                                <TableCell key={i} align={i === 0 ? 'left' : 'center'}>
                                    {col}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dailyReportData.fields.map((row) => (
                            <TableRow hover key={row.student_id}>
                                <TableCell align="left" className="title-case">
                                    {patientNameOrderValueGetter(row)}
                                </TableCell>
                                <TableCell align="center">{row.classroom_name}</TableCell>
                                <TableCell align="center">
                                    {row.present_status === 'Present' ? (
                                        <>
                                            Present
                                            <CheckCircleOutline sx={{ color: 'green', verticalAlign: 'middle', mr: 1 }} />
                                        </>
                                    ) : row.present_status === 'Absent' ? (
                                        <>
                                            Absent
                                            <Cancel sx={{ color: 'red', verticalAlign: 'middle', mr: 1 }} />
                                        </>
                                    ) : (
                                        row.present_status || 'N/A'
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {row.check_in_time ? utcToLocal(row.check_in_time, GLOBAL_DATE_TIME_FORMAT) : 'N/A'}
                                </TableCell>
                                <TableCell align="center">
                                    {row.check_out_time ? utcToLocal(row.check_out_time, GLOBAL_DATE_TIME_FORMAT) : 'N/A'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Stack justifyContent="flex-end">
                    <Pagination
                        color="primary"
                        count={Math.ceil((dailyReportData?.fields?.[0]?.total_count || 1) / ROWS_PER_PAGE)}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        sx={{ mt: 3, mb: 3, '& .MuiPagination-ul': { flexWrap: 'nowrap', justifyContent: 'flex-end' } }}
                        showFirstButton
                        showLastButton
                    />
                </Stack>
            </TableContainer>
        );
    }, [dailyReportData]);

    return (
        <>
            {/* Filter Form */}
            <Grid container spacing={2} mt={0.25}>
                <Grid item sm={12} md={2}>
                    <DatePicker
                        required
                        label="Date"
                        inputFormat={GLOBAL_DATE_FORMAT}
                        maxDate={new Date()}
                        value={formik.values.startDate}
                        onChange={(v) => formik.setFieldValue('startDate', v)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                variant="outlined"
                                error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                                helperText={formik.touched.startDate && formik.errors.startDate}
                            />
                        )}
                    />
                </Grid>

                <Grid item sm={12} md={2}>
                    <LocationDropdown
                        required
                        sx={{ width: '100%' }}
                        filters={{ is_session: true }}
                        value={formik.values.location}
                        onChange={(value) => {
                            formik.setFieldValue('location', value);
                            formik.setFieldValue('classroom', null);
                        }}
                        error={formik.touched.location && Boolean(formik.errors.location)}
                        helperText={formik.touched.location && formik.errors.location}
                    />
                </Grid>

                <Grid item sm={12} md={2}>
                    <Autocomplete
                        loading={classroomLoading || classroomFetching}
                        options={sortedClassrooms}
                        groupBy={(opt) => opt?.location?.name}
                        getOptionLabel={(opt) => opt?.name || ''}
                        value={formik.values.classroom}
                        onChange={(_, val) => formik.setFieldValue('classroom', val)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Classroom"
                                error={formik.touched.classroom && Boolean(formik.errors.classroom)}
                                helperText={formik.touched.classroom && formik.errors.classroom}
                            />
                        )}
                    />
                </Grid>

                <Grid item sm={12} md={2}>
                    <Autocomplete
                        fullWidth
                        options={[
                            { id: 'present', name: 'Present' },
                            { id: 'absent', name: 'Absent' },
                            { id: 'picked_up', name: 'Picked Up' }
                        ]}
                        value={formik.values.status}
                        onChange={(_, val) => formik.setFieldValue('status', val)}
                        getOptionLabel={(opt) => opt?.name || ''}
                        renderInput={(params) => <TextField {...params} label="Status" />}
                    />
                </Grid>

                <Grid item sm={12} md={2}>
                    <TextField
                        fullWidth
                        label="Student"
                        placeholder="Search Student"
                        value={formik.values.student}
                        onChange={(e) => formik.setFieldValue('student', e.target.value)}
                        error={formik.touched.student && Boolean(formik.errors.student)}
                        helperText={formik.touched.student && formik.errors.student}
                        InputProps={{
                            endAdornment: formik.values.student && (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => formik.setFieldValue('student', '')} edge="end" size="small">
                                        <CloseIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Grid>

                <Grid item sm={12} md={3}>
                    <Stack direction="row" spacing={2} sx={{ minHeight: 50 }}>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setPage(1);
                                formik.handleSubmit();
                            }}
                        >
                            Search
                        </Button>
                        <Button variant="contained" onClick={handleResetForm}>
                            Reset
                        </Button>
                        {dailyReportData?.fields?.length > 0 && hasPermission(['attendance_report_download']) && (
                            <ExportColumnData handleDownload={handleDownload} betaBtn />
                        )}
                    </Stack>
                </Grid>
            </Grid>

            {/* Summary Cards */}
            <Grid container spacing={2} mt={1} mb={1}>
                {cardData.map((card, i) => (
                    <Grid item xs={12} sm={3} key={i}>
                        <AmountCard
                            sx={{
                                height: 60,
                                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                                borderRadius: '8px'
                            }}
                            loading={dailyReportLoading}
                            label={card.label}
                            isAmount={false}
                            amount={card.amount}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Loader */}
            {(dailyReportLoading || downloadLoading) && (
                <Stack justifyContent="center" mt={1}>
                    <CircularProgress />
                </Stack>
            )}

            {/* Table */}
            {dailyReportData?.fields?.length ? renderTable : <NoData title="No Data Found." />}
        </>
    );
};

export default memo(DailyReport);
